<template>
  <!-- [ID-338] EditorialRanking — Overview field. Reads info.profile.editorial_ranking.
       Selecting a new value emits 'change-requested'; does NOT mutate the form directly. -->
  <span
    v-if="currentValue || options.length"
    :key="keys.EditorialRanking"
    v-b-tooltip="displayRankingTooltip"
  >
    <font-awesome-icon
      :icon="['fas', 'circle']"
      :class="iconColorClass"
    />&nbsp;
    <span
      v-if="!state || isLocked"
      style="width: 120px"
      @dblclick.prevent.stop="toggle"
    >
      {{ displayRanking }}
    </span>
    <b-form-select
      v-else
      v-model="selectedValue"
      :options="options"
      size="sm"
      style="width: 120px"
      @change="onValueChange"
    />
    <b-link
      v-if="!isLocked"
      @click="toggle"
    >
      <font-awesome-icon
        size="sm"
        class="ml-2"
        :class="state ? 'text-success' : 'text-danger'"
        :icon="['fas', state ? 'check' : 'pen']"
      />
    </b-link>

    <!-- [ID-338] EditorialRankingModal — confirmation dialog with mandatory reason -->
    <editorial-ranking-modal
      v-model="editorialRankingDialogState"
      :user-id="itemId"
      :editorial-ranking="pendingEditorialRanking"
      @submitted="onEditorialRankingSubmitted"
      @rejected="onEditorialRankingRejected"
    />
  </span>
</template>

<script>
import { mapComputedStateFields } from '~/mixins/globalForm'
import EditorialRankingModal from '@/components/forms/CustomerEditForm/Modals/EditorialRanking.vue'

export default {
  name: 'EditorialRanking',
  components: { EditorialRankingModal },
  props: {
    itemId: {
      type: [Number, String],
      required: true
    },
    options: {
      type: Array,
      default: () => ([])
    },
    repo: {
      type: String,
      required: true,
      default: ''
    },
    idx: {
      type: Number,
      default: -1
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    }
  },
  data () {
    const item = this.$store.state.forms.customerEdit.forms.find(f => f.id === this.itemId)
    return {
      state: false,
      previousValue: item?.info?.profile?.editorial_ranking || '',
      selectedValue: item?.info?.profile?.editorial_ranking || '',
      editorialRankingDialogState: false,
      pendingEditorialRanking: null
    }
  },
  computed: {
    ...mapComputedStateFields('customerEdit', []),
    // [ID-338] Reads from info.profile (persisted state), NOT from the dirty form
    currentValue () {
      return this.value
    },
    displayRanking () {
      const item = this.options.find(i => i.value === this.selectedValue)
      return item ? item.text : this.$t('fCustomer_editRanking_a_zero')
    },
    displayRankingTooltip () {
      const item = this.options.find(i => i.value === this.selectedValue)
      return item ? item.tooltip : this.$t('fCustomer_editRanking_a_zero_tooltip')
    },
    // [ID-338] Color map: value → scoped CSS class. Backend options are { value, text } only;
    // color is a frontend concern keyed on the stable value strings.
    // Update this map if the set of valid values changes.
    iconColorClass () {
      const colorMap = {
        IDEAL: 'er-color-green',
        STANDART: 'er-color-yellow',
        RISK: 'er-color-orange',
        TOXIC: 'er-color-red'
      }
      return colorMap[this.selectedValue] || ''
    }
  },
  watch: {
    // Keep local selectedValue in sync when info is refreshed (e.g. after dialog submit)
    currentValue (n, o) {
      this.previousValue = o
      this.selectedValue = n
      this.state = false
    }
  },
  methods: {
    toggle () {
      this.state = !this.state
    },
    onValueChange (newValue) {
      this.onEditorialRankingChangeRequested({
        oldValue: this.previousValue,
        newValue: this.selectedValue
      })
      this.state = false
    },
    // [ID-338] Triggered when user selects a new editorial_ranking value.
    // Opens confirmation dialog; if cancelled, EditorialRanking component auto-resets via callbacks.
    onEditorialRankingChangeRequested (newValue) {
      this.pendingEditorialRanking = newValue
      this.editorialRankingDialogState = true
    },
    onEditorialRankingSubmitted () {
      this.previousValue = this.selectedValue
      this.$emit('submited')
    },
    onEditorialRankingRejected () {
      this.selectedValue = this.previousValue
      this.$emit('rejected')
    }
  }
}
</script>

<style scoped>
/* [ID-338] Editorial ranking color indicators — prefixed 'er-' to avoid collisions.
   Hex values mirror parent-scoped utilities (.text-light-green, .text-orange, .text-red). */
.er-color-green  { color: #1cc88a; } /* IDEAL    — Green  */
.er-color-yellow { color: #f6c23e; } /* STANDART — Yellow */
.er-color-orange { color: #fd7e14; } /* RISK     — Orange */
.er-color-red    { color: #be2617; } /* TOXIC    — Red    */
</style>
